import React, { useState, useMemo, useEffect, Suspense, lazy } from "react";
import CssBaseline from "@mui/material/CssBaseline";
import { Container, useMediaQuery, CircularProgress, Box } from "@mui/material";
import { DataProvider } from "./contexts/DataContext";
import { getDesignTokens, getThemedComponents } from "./conf/theme.ts";
import {
  createTheme,
  ThemeProvider,
  responsiveFontSizes,
} from "@mui/material/styles";
import { deepmerge } from "@mui/utils";
import { ColorModeContext } from "./contexts/ColorModeContext";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import SecureRoute from "./components/SecureRoute.js";
import ResponsiveAppBar from "./components/appBar";
import LoginDialog from "./components/LoginDialog";
import CachedUploadsNotification from "./components/CachedUploadsNotification";

// Only use code splitting for major route-based components
const Home = lazy(() => import(/* webpackChunkName: "home" */ './pages/Home'));
const Contact = lazy(() => import(/* webpackChunkName: "home" */ './pages/Contact.js'));
const FAQPage = lazy(() => import(/* webpackChunkName: "faq" */ './pages/FAQPage'));
const Participant = lazy(() => import(/* webpackChunkName: "participant" */ './pages/Participant'));
const ParticipantRegister = lazy(() => import(/* webpackChunkName: "participant" */ './pages/ParticipantRegister'));
const Admin = lazy(() => import(/* webpackChunkName: "admin" */ './pages/Admin'));

// Loading component for suspense fallback
const LoadingFallback = () => (
  <Box 
    sx={{ 
      display: 'flex', 
      justifyContent: 'center', 
      alignItems: 'center', 
      height: 'calc(100vh - 64px)' 
    }}
  >
    <CircularProgress />
  </Box>
);

function App() {
  const [mode, setMode] = useState("light");
  const [loginOpen, setLoginOpen] = useState(false);
  const prefersDarkMode = useMediaQuery("(prefers-color-scheme: dark)");

  useEffect(() => {
    setMode("light");
    //setMode(prefersDarkMode ? "dark" : "light");
  }, [prefersDarkMode]);

  let theme = useMemo(
    () =>
      responsiveFontSizes(
        createTheme(deepmerge(getDesignTokens(mode), getThemedComponents(mode)))
      ),
    [mode]
  );

  const colorMode = useMemo(
    () => ({
      toggleColorMode: () => {
        setMode((prevMode) => (prevMode === "light" ? "dark" : "light"));
      },
    }),
    []
  );

  const handleLoginClick = () => {
    setLoginOpen(true);
  };

  const handleLoginClose = () => {
    setLoginOpen(false);
  };

  return (
    <ColorModeContext.Provider value={colorMode}>
      <CssBaseline />
      <ThemeProvider theme={theme}>
        <DataProvider>
          <BrowserRouter>
            <ResponsiveAppBar onLoginClick={handleLoginClick} />
            <LoginDialog open={loginOpen} onClose={handleLoginClose} />
            <CachedUploadsNotification />
            <Suspense fallback={<LoadingFallback />}>
              <Routes>
                {/* Admin/PI routes */}
                <Route
                  path="/admin"
                  element={
                    <SecureRoute
                      roleRequired={["Relmed.Admin", "Relmed.PI"]}
                      element={<Admin />}
                    />
                  }
                />

                {/* Public routes */}
                <Route path="/contact" element={<Contact />} />
                <Route path="/faq" element={<FAQPage />} />
                <Route
                  path="/"
                  element={<Home onLoginClick={handleLoginClick} />}
                />
                <Route
                  path="/login"
                  element={
                    <Navigate to="/" state={{ showLogin: true }} replace />
                  }
                />

                {/* Participant routes */}
                <Route
                  path="/participant"
                  element={
                    <SecureRoute 
                      roleRequired={["Relmed.Ppt"]} 
                      element={<Participant />}
                      excludeRoles={["Relmed.Admin", "Relmed.PI"]} 
                    />
                  }
                />
                <Route path="/register" element={<ParticipantRegister />} />

                {/* Catch all */}
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
            </Suspense>
          </BrowserRouter>
        </DataProvider>
      </ThemeProvider>
    </ColorModeContext.Provider>
  );
}

export default App;
